import { inject, Injectable, LOCALE_ID, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { ConfigService } from './config.service';
import { Env } from '../dto/enum/env.enum';
import { NzMessageService } from 'ng-zorro-antd/message';
import { SentryService } from './sentry.service';
import loadjs from 'loadjs';

interface DataLayerWindow extends Window {
  dataLayer: unknown[];
  // eslint-disable-next-line @typescript-eslint/ban-types
  gtag: Function;
}

declare let window: DataLayerWindow;

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private readonly sentryService: SentryService = inject(SentryService);
  private readonly configService: ConfigService = inject(ConfigService);
  private readonly nzMessageService: NzMessageService =
    inject(NzMessageService);
  private readonly localeId: string = inject(LOCALE_ID);
  private readonly document: Document = inject<Document>(DOCUMENT);
  private readonly platformId: object = inject(PLATFORM_ID);

  public constructor() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    window.dataLayer = window.dataLayer || [];
    window.gtag = function (): void {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };
    window.gtag('js', new Date());
    window.gtag('config', this.configService.googleTagId);

    if (Env.PROD !== this.configService.env) {
      return;
    }

    this.loadGTagScript();
  }

  public event(eventName: string, params?: object): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    window.gtag('event', eventName, {
      ...params,
      locale: this.localeId,
      appVersion: this.configService.appVersion,
    });
  }

  private loadGTagScript(): void {
    if (this.document.getElementById('gtag')) {
      return;
    }

    // https://www.googletagmanager.com/gtag/js?id={this._configService.googleTagId}
    loadjs('gtag.js', {
      async: true,
      numRetries: this.configService.retryCount,
      error: (): void => {
        this.nzMessageService.error(
          $localize`:@@google-analytics-script-error:An error occurred during loading Google Analytics script`
        );
        console.error(
          'An error occurred during loading Google Analytics script'
        );
        this.sentryService.captureError({
          error: 'An error occurred during loading Google Analytics script',
          severityLevel: 'warning',
          transactionName: 'LOAD_GTAG_SCRIPT_ERROR',
        });
      },
      before: (path: string, scriptEl: HTMLElement): boolean => {
        const htmlElement: HTMLElement | null =
          this.document.getElementById('gtag');
        if (htmlElement) {
          htmlElement.parentElement?.removeChild(htmlElement);
        }
        scriptEl.id = 'gtag';
        return true;
      },
    });
  }
}
